import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";

export default function ValidateTransferts({
  apiBase = "https://appapi.unpaidfinancial.com/api",
}) {
  // --- state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // message flash temporaire
  const [flash, setFlash] = useState(null); // { type: 'success'|'error'|'info', text: string }

  // filtres réduits
  const [email, setEmail] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  // pagination meta
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);

  // actions
  const [actingId, setActingId] = useState(null);
  const [busy, setBusy] = useState(false);

  // tri interne (fixe, non affiché)
  const sort = "created_at";
  const order = "desc";
  const perPage = 20;

  // --- http
  const http = useMemo(() => {
    const instance = axios.create({ baseURL: apiBase });
    instance.interceptors.request.use((cfg) => {
      const token =
        localStorage.getItem("accessToken") || localStorage.getItem("bank_token");
      if (token) cfg.headers.Authorization = `Bearer ${token}`;
      return cfg;
    });
    return instance;
  }, [apiBase]);

  // --- helpers
  const fmtMoney = (n, dev = "EUR") =>
    `${Number(n ?? 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${dev}`;

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso ?? "";
    }
  };

  const apiError = (e) =>
    e?.response?.data?.error ||
    e?.response?.data?.message ||
    e?.message ||
    "Une erreur est survenue.";

  const showFlash = useCallback((type, text) => {
    setFlash({ type, text });
    setTimeout(() => setFlash(null), 3000);
  }, []);

  // --- fetch
  const fetchData = useCallback(
    async (overridePage = null) => {
        if (from && to && from > to) {
            setErr("La date de début doit être antérieure ou égale à la date de fin.");
            return;
        }
        setLoading(true);
        setErr("");
        try {
            const { data } = await http.get("/bank/transfers", {
                params: {
                    status: "en_attente",
                    type: "debit",
                    sort,
                    order,
                    per_page: perPage,
                    page: overridePage ?? page,
                    ...(email ? { search: email } : {}),
                    ...(from ? { date_from: from } : {}),
                    ...(to ? { date_to: to } : {}),
                     exclude_type: 'admin', // Filtrage côté serveur
                },
            });
            setRows(data?.data ?? []);
            setTotal(data?.total ?? 0);
            setLastPage(data?.last_page ?? 1);
        } catch (e) {
            setErr(apiError(e));
        } finally {
            setLoading(false);
        }
    },
    [http, email, from, to, page]
);

  // --- actions directes
  const approve = async (id) => {
    const item = rows.find((r) => r.id === id);
    if (!item || busy) return;
    setBusy(true);
    setActingId(id);
    const before = rows;
    setRows((prev) => prev.filter((t) => t.id !== id)); // optimistic
    try {
      await http.post(`/bank/transactions/${id}/approve`);
      showFlash("success", `Virement #${id} validé avec succès.`);
    } catch (e) {
      setRows(before); // rollback
      setErr(apiError(e));
      showFlash("error", `Échec de la validation (#${id}).`);
    } finally {
      setBusy(false);
      setActingId(null);
    }
  };

  const refuse = async (id) => {
    const item = rows.find((r) => r.id === id);
    if (!item || busy) return;
    setBusy(true);
    setActingId(id);
    const before = rows;
    setRows((prev) => prev.filter((t) => t.id !== id)); // optimistic
    try {
      await http.post(`/bank/transactions/${id}/refuse`, { reason: null });
      showFlash("success", `Virement #${id} refusé avec succès.`);
    } catch (e) {
      setRows(before); // rollback
      setErr(apiError(e));
      showFlash("error", `Échec du refus (#${id}).`);
    } finally {
      setBusy(false);
      setActingId(null);
    }
  };

  // filtres
  const applyFilters = () => {
    setPage(1);
    fetchData(1);
  };
  const clearFilters = () => {
    setEmail("");
    setFrom("");
    setTo("");
    setPage(1);
    fetchData(1);
  };

  // --- UI
  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto relative">
      {/* Flash simple, centré en haut */}
      <FlashBanner flash={flash} />

      {/* header + toolbar compacte */}
      <div className="mb-4 md:mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-2xl font-semibold">Validation des transferts</h1>
            <p className="text-sm text-gray-500">
              Statut : <b>en_attente</b> • Type : <b>debit</b> • Affichage : <b>cartes</b>
            </p>
          </div>
          <button
            onClick={() => fetchData()}
            disabled={loading}
            className="hidden md:inline-flex px-4 h-10 rounded-lg border bg-white hover:bg-gray-50 disabled:opacity-60"
          >
            Actualiser
          </button>
        </div>

        {/* TOOLBAR (email + dates) */}
        <div className="bg-white border rounded-xl p-2">
          <div className="flex flex-wrap items-end gap-2 md:gap-3">
            <Field label="Email (émetteur/bénéficiaire)">
              <input
                placeholder="ex: user@domaine.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                className="w-56 md:w-72 border rounded-lg px-3 h-10 focus:ring-2 focus:ring-black/10"
              />
            </Field>
            <Field label="Depuis">
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="w-40 border rounded-lg px-3 h-10 focus:ring-2 focus:ring-black/10"
              />
            </Field>
            <Field label="Jusqu’au">
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="w-40 border rounded-lg px-3 h-10 focus:ring-2 focus:ring-black/10"
              />
            </Field>

            <div className="ms-auto flex items-center gap-2">
              <button
                onClick={applyFilters}
                disabled={loading}
                className="px-4 h-10 rounded-lg bg-black text-white hover:bg-black/90 disabled:opacity-60"
              >
                Rechercher
              </button>
              <button
                onClick={clearFilters}
                disabled={loading || (!email && !from && !to)}
                className="px-3 h-10 rounded-lg border bg-white hover:bg-gray-50 disabled:opacity-40"
                title="Réinitialiser la recherche"
              >
                Effacer
              </button>
              <button
                onClick={() => fetchData()}
                disabled={loading}
                className="px-4 h-10 rounded-lg border bg-white hover:bg-gray-50 md:hidden disabled:opacity-60"
              >
                Actualiser
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* erreurs globales */}
      {err && (
        <div className="mb-3 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
          {String(err)}
        </div>
      )}

      {/* skeleton */}
      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="animate-pulse p-4 rounded-xl border bg-white">
              <div className="h-3 w-24 bg-gray-200 rounded mb-2" />
              <div className="h-3 w-40 bg-gray-200 rounded mb-2" />
              <div className="h-3 w-32 bg-gray-200 rounded mb-4" />
              <div className="h-10 w-full bg-gray-200 rounded" />
            </div>
          ))}
        </div>
      )}

      {/* empty */}
      {!loading && rows.length === 0 && (
        <div className="p-10 text-center text-gray-500 border rounded-xl bg-white">
          Aucune demande en attente.
        </div>
      )}

      {/* CARTES (mobile + desktop) */}
      {!loading && rows.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((t) => {
            const emitter = t?.from_account?.user?.email ?? "—";
            const receiver =
              t?.to_account?.user?.email ?? (t?.to_account_id ? "—" : "Externe");
            const dev = t?.from_account?.devise ?? "EUR";
            return (
              <article
                key={t.id}
                className="p-4 rounded-xl border bg-white shadow-sm flex flex-col"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] px-2 py-1 rounded-full bg-amber-100 text-amber-800 font-medium">
                    en_attente
                  </span>
                  <span className="text-xs text-gray-500">{fmtDate(t.created_at)}</span>
                </div>

                <div className="space-y-1 text-sm flex-1">
                  <Row label="Émetteur" value={emitter} />
                  <Row label="Bénéficiaire" value={receiver} />
                  <Row label="Montant" value={fmtMoney(t.amount, dev)} bold />
                  <div className="text-gray-700">
                    {t.description || <span className="text-gray-400">—</span>}
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-2">
                  <BtnGreen
                    loading={busy && actingId === t.id}
                    onClick={() => approve(t.id)}
                  >
                    {busy && actingId === t.id ? "Validation…" : "Valider"}
                  </BtnGreen>
                  <BtnRed
                    loading={busy && actingId === t.id}
                    onClick={() => refuse(t.id)}
                  >
                    {busy && actingId === t.id ? "Refus…" : "Refuser"}
                  </BtnRed>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* pagination */}
      {!loading && rows.length > 0 && (
        <div className="mt-4 flex flex-col md:flex-row items-center justify-between gap-3 text-sm">
          <div className="text-gray-600">
            Total : <b>{total}</b> • Page <b>{page}</b> / {lastPage}
          </div>
          <div className="flex items-center gap-2">
            <button
              className="px-4 h-10 rounded-lg border bg-white hover:bg-gray-50 disabled:opacity-40"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Précédent
            </button>
            <button
              className="px-4 h-10 rounded-lg border bg-white hover:bg-gray-50 disabled:opacity-40"
              disabled={page >= lastPage || loading}
              onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
            >
              Suivant
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* —— Sub components —— */

function FlashBanner({ flash }) {
  if (!flash) return null;
  const palette =
    flash.type === "success"
      ? "bg-emerald-50 border-emerald-200 text-emerald-900"
      : flash.type === "error"
      ? "bg-red-50 border-red-200 text-red-900"
      : "bg-gray-50 border-gray-200 text-gray-900";

  return (
    <div
      className={`fixed left-1/2 -translate-x-1/2 top-3 w-[calc(100%-24px)] max-w-xl border rounded-xl shadow-sm px-4 py-2 text-sm ${palette}`}
      role="status"
      aria-live="polite"
      style={{ pointerEvents: "none" }}
    >
      <div className="text-center font-medium">{flash.text}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col">
      <label className="text-[11px] text-gray-500">{label}</label>
      {children}
    </div>
  );
}
function Row({ label, value, bold }) {
  return (
    <div>
      <span className="text-gray-500">{label}:</span>{" "}
      <span className={bold ? "font-semibold" : ""}>{value}</span>
    </div>
  );
}
function BtnGreen({ children, onClick, loading }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="px-4 h-10 rounded-lg text-white font-semibold border active:scale-[0.99] disabled:opacity-60"
      style={{ backgroundColor: "#16a34a", borderColor: "#16a34a" }}
      onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#15803d")}
      onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#16a34a")}
    >
      {children}
    </button>
  );
}
function BtnRed({ children, onClick, loading }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="px-4 h-10 rounded-lg text-white font-semibold bg-red-600 border border-red-600 hover:bg-red-700 active:scale-[0.99] disabled:opacity-60"
    >
      {children}
    </button>
  );
}
